import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { LanguageSelector } from './LanguageSelector';
import { AppStoreButtons } from './AppStoreButtons';
import { MomentSearchSection } from './MomentSearchSection';
import { firebaseService, getFirebaseApp } from '../services/firebase.service';
import '../styles/LandingPage.css';

// Get app URLs from environment variables
const FAN_APP_URL = import.meta.env.VITE_FAN_APP_URL || 'http://localhost:5173';
const OWNER_APP_URL = import.meta.env.VITE_OWNER_APP_URL || 'http://localhost:5174';
const SPOTTER_APP_URL = import.meta.env.VITE_SPOTTER_APP_URL || 'http://localhost:5175';

interface CategoryCarouselItem {
  label?: string;
  url: string;
  caption?: string;
  showInFanApp?: boolean;
  showOnLandingPage?: boolean;
}

interface LandingSportCategory {
  id: string;
  displayName?: string;
  icon?: string;
  fanLandingCarouselItems?: CategoryCarouselItem[];
}

interface LandingCarouselVideo {
  id: string;
  categoryId: string;
  categoryLabel: string;
  url: string;
  caption: string;
  videoId: string;
}

const extractYouTubeVideoId = (url: string): string | null => {
  if (!url) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&]+)/,
    /(?:youtube\.com\/live\/)([^?]+)/,
    /(?:youtube\.com\/embed\/)([^?]+)/,
    /(?:youtu\.be\/)([^?]+)/,
    /(?:youtube\.com\/shorts\/)([^?]+)/,
    /(?:youtube\.com\/v\/)([^?]+)/,
  ];

  return patterns.map((pattern) => url.match(pattern)?.[1] ?? null).find(Boolean) ?? null;
};

const stripLeadingEmoji = (value: string): string =>
  value.replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u, '').trim();

const COVERAGE_PREVIEW_VISIBLE_TAGS = 7;
const COVERAGE_PREVIEW_SLOT_COUNT = 8;

type CoveragePreviewTag = {
  label: string;
  isMore?: boolean;
  isPlaceholder?: boolean;
};

const buildCoveragePreviewTags = (tags: string[]): CoveragePreviewTag[] => {
  const normalizedTags = tags
    .map(stripLeadingEmoji)
    .filter((tag): tag is string => tag.length > 0);

  const previewTags: CoveragePreviewTag[] = normalizedTags
    .slice(0, COVERAGE_PREVIEW_VISIBLE_TAGS)
    .map((label) => ({ label }));

  if (normalizedTags.length > COVERAGE_PREVIEW_VISIBLE_TAGS) {
    previewTags.push({ label: '+ More', isMore: true });
  }

  while (previewTags.length < COVERAGE_PREVIEW_SLOT_COUNT) {
    previewTags.push({ label: '', isPlaceholder: true });
  }

  return previewTags.slice(0, COVERAGE_PREVIEW_SLOT_COUNT);
};

const CoverageIcon: React.FC<{ kind: 'sports' | 'esports' | 'influencers' | 'news' }> = ({ kind }) => {
  const commonProps = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (kind) {
    case 'sports':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...commonProps} d="M8 21h8" />
          <path {...commonProps} d="M12 17v4" />
          <path {...commonProps} d="M7 4h10v3a5 5 0 0 1-10 0V4Z" />
          <path {...commonProps} d="M7 5H5a2 2 0 0 0 2 4" />
          <path {...commonProps} d="M17 5h2a2 2 0 0 1-2 4" />
        </svg>
      );
    case 'esports':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...commonProps} d="M6 10h12a3 3 0 0 1 2.4 4.8l-1.7 2.2a3 3 0 0 1-2.4 1.2H7.7a3 3 0 0 1-2.4-1.2l-1.7-2.2A3 3 0 0 1 6 10Z" />
          <path {...commonProps} d="M8 10 9.5 7.5A2 2 0 0 1 11.2 6h1.6a2 2 0 0 1 1.7 1.5L16 10" />
          <path {...commonProps} d="M9 14h2" />
          <path {...commonProps} d="M10 13v2" />
          <circle {...commonProps} cx="15.5" cy="13.5" r="0.5" />
          <circle {...commonProps} cx="17.5" cy="15.5" r="0.5" />
        </svg>
      );
    case 'influencers':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...commonProps} d="M4 10a2 2 0 0 1 2-2h2l5-3v14l-5-3H6a2 2 0 0 1-2-2v-4Z" />
          <path {...commonProps} d="M16 9a5 5 0 0 1 0 6" />
          <path {...commonProps} d="M18.5 6.5a8.5 8.5 0 0 1 0 11" />
        </svg>
      );
    case 'news':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...commonProps} d="M6 5h11a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8a3 3 0 0 1-3-3V7a2 2 0 0 1 1-2Z" />
          <path {...commonProps} d="M8 9h8" />
          <path {...commonProps} d="M8 12h8" />
          <path {...commonProps} d="M8 15h5" />
          <path {...commonProps} d="M5 16a3 3 0 0 0 3 3" />
        </svg>
      );
  }
};

export const LandingPage: React.FC = () => {
  const intl = useIntl();
  // Video player state - lazy load iframe on click
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoKey, setVideoKey] = useState(0);
  const [isNavHidden, setIsNavHidden] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [landingCarouselVideos, setLandingCarouselVideos] = useState<LandingCarouselVideo[]>([]);
  const [playingLandingVideoIds, setPlayingLandingVideoIds] = useState<Record<string, boolean>>({});
  const heroRef = useRef<HTMLElement | null>(null);
  const videoSectionRef = useRef<HTMLElement | null>(null);
  const lastScrollY = useRef(0);
  const sportsTags = intl
    .formatMessage({ id: 'landing.whatWeCover.sports.sports' })
    .split(',')
    .map((item) => item.trim());
  const esportsTags = intl
    .formatMessage({ id: 'landing.whatWeCover.esports.games' })
    .split(',')
    .map((item) => item.trim());
  const influencerTags = intl
    .formatMessage({ id: 'landing.whatWeCover.influencers.types' })
    .split(',')
    .map((item) => item.trim());
  const newsTags = intl
    .formatMessage({ id: 'landing.whatWeCover.news.types' })
    .split(',')
    .map((item) => item.trim());

  const coverageItems = [
    {
      key: 'sports',
      cardClass: 'sports-card',
      icon: <CoverageIcon kind="sports" />,
      titleId: 'landing.whatWeCover.sports.title',
      tags: sportsTags,
      previewTags: buildCoveragePreviewTags(sportsTags),
      momentsTitleId: 'landing.whatWeCover.sports.keyMoments',
      moments: [
        { icon: '⚽', id: 'landing.whatWeCover.sports.moment1' },
        { icon: '🟥', id: 'landing.whatWeCover.sports.moment2' },
        { icon: '🎯', id: 'landing.whatWeCover.sports.moment3' },
        { icon: '🏆', id: 'landing.whatWeCover.sports.moment4' },
        { icon: '⏱️', id: 'landing.whatWeCover.sports.moment5' },
        { icon: '💥', id: 'landing.whatWeCover.sports.moment6' }
      ]
    },
    {
      key: 'esports',
      cardClass: 'esports-card',
      icon: <CoverageIcon kind="esports" />,
      titleId: 'landing.whatWeCover.esports.title',
      tags: esportsTags,
      previewTags: buildCoveragePreviewTags(esportsTags),
      momentsTitleId: 'landing.whatWeCover.esports.keyMoments',
      moments: [
        { icon: '💀', id: 'landing.whatWeCover.esports.moment1' },
        { icon: '🎯', id: 'landing.whatWeCover.esports.moment2' },
        { icon: '⚡', id: 'landing.whatWeCover.esports.moment3' },
        { icon: '🏆', id: 'landing.whatWeCover.esports.moment4' },
        { icon: '🔥', id: 'landing.whatWeCover.esports.moment5' },
        { icon: '💣', id: 'landing.whatWeCover.esports.moment6' }
      ]
    },
    {
      key: 'influencers',
      cardClass: 'influencer-card',
      icon: <CoverageIcon kind="influencers" />,
      titleId: 'landing.whatWeCover.influencers.title',
      tags: influencerTags,
      previewTags: buildCoveragePreviewTags(influencerTags),
      momentsTitleId: 'landing.whatWeCover.influencers.keyMoments',
      moments: [
        { icon: '🤯', id: 'landing.whatWeCover.influencers.moment1' },
        { icon: '🎤', id: 'landing.whatWeCover.influencers.moment2' },
        { icon: '🎁', id: 'landing.whatWeCover.influencers.moment3' },
        { icon: '😂', id: 'landing.whatWeCover.influencers.moment4' },
        { icon: '🔥', id: 'landing.whatWeCover.influencers.moment5' },
        { icon: '💬', id: 'landing.whatWeCover.influencers.moment6' }
      ]
    },
    {
      key: 'news',
      cardClass: 'news-card',
      icon: <CoverageIcon kind="news" />,
      titleId: 'landing.whatWeCover.news.title',
      tags: newsTags,
      previewTags: buildCoveragePreviewTags(newsTags),
      momentsTitleId: 'landing.whatWeCover.news.keyMoments',
      moments: [
        { icon: '💥', id: 'landing.whatWeCover.news.moment1' },
        { icon: '🔥', id: 'landing.whatWeCover.news.moment2' },
        { icon: '📊', id: 'landing.whatWeCover.news.moment3' },
        { icon: '🎯', id: 'landing.whatWeCover.news.moment4' },
        { icon: '⚡', id: 'landing.whatWeCover.news.moment5' },
        { icon: '🎙️', id: 'landing.whatWeCover.news.moment6' }
      ]
    }
  ];

  const [activeCoverageKey, setActiveCoverageKey] = useState<string | null>(null);

  const landingCarouselTitle = intl.formatMessage({
    id: 'landing.categoryCarousel.title',
    defaultMessage: 'Featured Videos',
  });
  const landingCarouselSubtitle = intl.formatMessage({
    id: 'landing.categoryCarousel.subtitle',
    defaultMessage: 'Curated fan videos',
  });

  const shuffledLandingCarouselVideos = useMemo(() => {
    const shuffled = [...landingCarouselVideos];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
    }
    return shuffled;
  }, [landingCarouselVideos]);

  const mainInfoFeatures = [
    intl.formatMessage({ id: 'landing.mainInfo.features.aiDetection' }),
    intl.formatMessage({ id: 'landing.mainInfo.features.globalScale' }),
    intl.formatMessage({ id: 'landing.mainInfo.features.instantDelivery' }),
    intl.formatMessage({ id: 'landing.mainInfo.features.multiPlatform' }),
  ];

  const fanFeatures = [
    intl.formatMessage({ id: 'landing.forYou.fan.feature1' }),
    intl.formatMessage({ id: 'landing.forYou.fan.feature2' }),
    intl.formatMessage({ id: 'landing.forYou.fan.feature3' }),
    intl.formatMessage({ id: 'landing.forYou.fan.feature4' }),
    intl.formatMessage({ id: 'landing.forYou.fan.feature5' }),
  ];

  const ownerFeatures = [
    intl.formatMessage({ id: 'landing.forYou.owner.feature1' }),
    intl.formatMessage({ id: 'landing.forYou.owner.feature2' }),
    intl.formatMessage({ id: 'landing.forYou.owner.feature3' }),
    intl.formatMessage({ id: 'landing.forYou.owner.feature4' }),
    intl.formatMessage({ id: 'landing.forYou.owner.feature5' }),
  ];

  const spotterFeatures = [
    intl.formatMessage({ id: 'landing.forYou.spotter.feature1' }),
    intl.formatMessage({ id: 'landing.forYou.spotter.feature2' }),
    intl.formatMessage({ id: 'landing.forYou.spotter.feature3' }),
    intl.formatMessage({ id: 'landing.forYou.spotter.feature4' }),
    intl.formatMessage({ id: 'landing.forYou.spotter.feature5' }),
  ];

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const handleChange = () => setIsMobile(media.matches);
    handleChange();
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    const threshold = 16;
    let ticking = false;
    const onScroll = () => {
      const currentY = window.scrollY;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const diff = currentY - lastScrollY.current;
          if (Math.abs(diff) > threshold) {
            if (diff > 0 && currentY > 80) {
              setIsNavHidden(true);
            } else {
              setIsNavHidden(false);
            }
            lastScrollY.current = currentY;
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);


  useEffect(() => {
    if (!videoSectionRef.current) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isMobile) {
          return;
        }
        if (entry.isIntersecting) {
          setIsVideoPlaying(true);
          setVideoKey((prev) => prev + 1);
        } else {
          setIsVideoPlaying(false);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(videoSectionRef.current);
    return () => observer.disconnect();
  }, [isMobile]);

  useEffect(() => {
    let isMounted = true;

    const loadLandingCarouselVideos = async () => {
      try {
        await firebaseService.initialize();
        const db = getFirestore(getFirebaseApp(), 'hypedirector-db');
        const categoriesDoc = await getDoc(doc(db, 'taxonomy', 'sport_categories'));

        if (!isMounted || !categoriesDoc.exists()) {
          return;
        }

        const rawCategories = Array.isArray(categoriesDoc.data()?.categories)
          ? (categoriesDoc.data()?.categories as LandingSportCategory[])
          : [];

        const sortedCategories = [...rawCategories].sort((a, b) =>
          (a.displayName || a.id).localeCompare(b.displayName || b.id),
        );

        const nextVideos = sortedCategories.flatMap((category) => (
          (category.fanLandingCarouselItems || [])
            .filter((item) => item.showOnLandingPage === true)
            .map((item, index) => {
              const videoId = extractYouTubeVideoId(item.url);
              if (!videoId) {
                return null;
              }

              return {
                id: `${category.id}-${index}-${videoId}`,
                categoryId: category.id,
                categoryLabel: category.displayName || category.id,
                url: item.url,
                caption: item.caption?.trim() || '',
                videoId,
              };
            })
            .filter((item): item is LandingCarouselVideo => item !== null)
        ));

        setLandingCarouselVideos(nextVideos);
      } catch (error) {
        console.warn('[LandingPage] Failed to load landing carousel videos:', error);
        if (isMounted) {
          setLandingCarouselVideos([]);
        }
      }
    };

    loadLandingCarouselVideos();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleNavLinkClick = () => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <div className="landing-page">
      <LanguageSelector />
      {/* Top Navigation Menu */}
      <nav className={`top-nav${isNavHidden ? ' is-hidden' : ''}`}>
        <button
          type="button"
          className="nav-toggle"
          aria-label={intl.formatMessage({ id: 'landing.nav.menu', defaultMessage: 'Menu' })}
          aria-expanded={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>
        <div className={`nav-links${isMobileMenuOpen ? ' is-open' : ''}`}>
          <Link to="/vision" className="nav-link" onClick={handleNavLinkClick}>
            <span className="nav-text">
              {intl.formatMessage({ id: 'landing.nav.vision', defaultMessage: 'Vision' })}
            </span>
          </Link>
          <a
            href={FAN_APP_URL}
            className="nav-link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleNavLinkClick}
          >
            <span className="nav-text">{intl.formatMessage({ id: 'landing.nav.forFans' })}</span>
          </a>
          <a
            href={OWNER_APP_URL}
            className="nav-link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleNavLinkClick}
          >
            <span className="nav-text">{intl.formatMessage({ id: 'landing.nav.forStreamOwners' })}</span>
          </a>
          <a
            href={SPOTTER_APP_URL}
            className="nav-link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleNavLinkClick}
          >
            <span className="nav-text">{intl.formatMessage({ id: 'landing.nav.forSpotters' })}</span>
          </a>
          <Link to="/contact" className="nav-link nav-link-contact" onClick={handleNavLinkClick}>
            <span className="nav-text">{intl.formatMessage({ id: 'landing.nav.contact' })}</span>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" ref={heroRef}>
        <div className="hero-background"></div>
        <div className="hero-content">
          <div className="logo">
            <img
              src="/logo-transparent-no-slogan.png"
              alt={intl.formatMessage({ id: 'landing.brand.alt' })}
              className="logo-image"
            />
          </div>
          <h1 className="hero-title">
            <span className="gradient-text">{intl.formatMessage({ id: 'landing.hero.title' })}</span>
          </h1>
          <p className="hero-subtitle">
            {intl.formatMessage({ id: 'landing.hero.subtitle1' })}{' '}
            <br />
            {intl.formatMessage({ id: 'landing.hero.subtitle2' })}{' '}
            <br />
            {intl.formatMessage({ id: 'landing.hero.subtitle3' })}
            <br />
            <br />
            <strong>{intl.formatMessage({ id: 'landing.hero.subtitle4' })}</strong>
          </p>
        </div>
      </section>

      <MomentSearchSection />

      {/* Main Info Section */}
      <section className="main-info">
        <div className="container">
          <div className="info-grid">
            <div className="info-text">
              <h2>{intl.formatMessage({ id: 'landing.mainInfo.title' })}</h2>
              <p>
                {intl.formatMessage({ id: 'landing.mainInfo.paragraph1' })}
                {' '}
                {intl.formatMessage({ id: 'landing.mainInfo.paragraph2' })}
              </p>
              <p>
                {intl.formatMessage({ id: 'landing.mainInfo.paragraph3' })}
                {' '}
                {intl.formatMessage({ id: 'landing.mainInfo.paragraph4' })}
                {' '}
                {intl.formatMessage({ id: 'landing.mainInfo.paragraph5' })}
              </p>
              <div className="info-features">
                {mainInfoFeatures.map((feature, index) => (
                  <div key={feature} className="feature-item">
                    <span className="feature-number">{index + 1}</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="info-visual">
              <div className="moment-card pulse">
                <div className="moment-header">
                  <span className="live-badge">{intl.formatMessage({ id: 'landing.mainInfo.momentCard.live' })}</span>
                  <div className="moment-actions">
                    <button className="cut-moment-btn">{intl.formatMessage({ id: 'landing.mainInfo.momentCard.cutToMoment' })}</button>
                    <button className="go-live-btn">{intl.formatMessage({ id: 'landing.mainInfo.momentCard.goLive' })}</button>
                  </div>
                </div>
                <div className="moment-content">
                  <div className="moment-icon">⚽</div>
                  <div className="moment-text">
                    <h3>{intl.formatMessage({ id: 'landing.mainInfo.momentCard.bigMoment' })}</h3>
                    <p>{intl.formatMessage({ id: 'landing.mainInfo.momentCard.teamExample' })}</p>
                  </div>
                </div>
            <div className="moment-actions moment-actions-mobile">
              <button className="cut-moment-btn">{intl.formatMessage({ id: 'landing.mainInfo.momentCard.cutToMoment' })}</button>
              <button className="go-live-btn">{intl.formatMessage({ id: 'landing.mainInfo.momentCard.goLive' })}</button>
            </div>
              </div>
              
              {/* No Spoilers Explanation */}
              <div className="no-spoilers-section">
                <h3>{intl.formatMessage({ id: 'landing.mainInfo.noSpoilers.title' })}</h3>
                <p dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'landing.mainInfo.noSpoilers.description' }) }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {shuffledLandingCarouselVideos.length > 0 && (
        <section className="landing-carousel-section">
          <div className="container">
            <h2 className="section-title">{landingCarouselTitle}</h2>
            <p className="section-subtitle">{landingCarouselSubtitle}</p>

            <div className="landing-carousel-track">
              {shuffledLandingCarouselVideos.map((video) => (
                <article key={video.id} className="landing-carousel-card">
                  {video.caption ? (
                    <div className="landing-carousel-card-header">
                      <div className="landing-carousel-caption">{video.caption}</div>
                    </div>
                  ) : null}

                  <div className="landing-carousel-video-frame">
                    {!playingLandingVideoIds[video.id] ? (
                      <button
                        type="button"
                        onClick={() => setPlayingLandingVideoIds((current) => ({ ...current, [video.id]: true }))}
                        className="video-thumbnail-button"
                        aria-label={intl.formatMessage({
                          id: 'landing.categoryCarousel.playButton',
                          defaultMessage: 'Play featured video',
                        })}
                      >
                        <img
                          src={`https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`}
                          alt={video.categoryLabel}
                          className="video-thumbnail-image"
                        />
                        <div className="play-button-overlay">
                          <div className="play-icon-circle">
                            <span className="play-icon">▶</span>
                          </div>
                        </div>
                      </button>
                    ) : (
                      <div className="video-iframe-container">
                        <iframe
                          src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1&mute=0&rel=0&playsinline=1`}
                          title={`${video.categoryLabel} featured video`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          className="video-iframe"
                        />
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Video Showcase Section */}
      <section className="video-showcase-section" ref={videoSectionRef}>
        <div className="container">
          <h2 className="section-title">
            {intl.formatMessage({ id: 'landing.videoShowcase.title' })}
          </h2>
          <p className="section-subtitle">
            {intl.formatMessage({ id: 'landing.videoShowcase.subtitle' })}
          </p>
          
          <div className="video-showcase-wrapper">
            <div className="video-showcase-frame">
              {!isVideoPlaying ? (
                // Thumbnail view - fast initial load
                <button 
                  onClick={() => setIsVideoPlaying(true)}
                  className="video-thumbnail-button"
                  aria-label={intl.formatMessage({ id: 'landing.videoShowcase.playButton' })}
                >
                  <img 
                    src="https://img.youtube.com/vi/H58L6kPsk_Q/maxresdefault.jpg" 
                    alt={intl.formatMessage({ id: 'landing.videoShowcase.videoAlt' })}
                    className="video-thumbnail-image"
                  />
                  <div className="play-button-overlay">
                    <div className="play-icon-circle">
                      <span className="play-icon">▶</span>
                    </div>
                  </div>
                </button>
              ) : (
                // iframe view - loads only when clicked
                <div className="video-iframe-container">
                  <iframe 
                    key={`landing-video-${videoKey}`}
                    width="100%" 
                    height="675"
                    src="https://www.youtube.com/embed/H58L6kPsk_Q?autoplay=1&rel=0&mute=1&playsinline=1"
                    title={intl.formatMessage({ id: 'landing.videoShowcase.playButton' })}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="video-iframe"
                  />
                </div>
              )}
            </div>
          </div>
          <AppStoreButtons className="video-app-store-buttons" />
        </div>
      </section>

      {/* For You Section - 3 Widgets */}
      <section id="for-you" className="for-you-section">
        <div className="container">
          <h2 className="section-title">{intl.formatMessage({ id: 'landing.forYou.title' })}</h2>
          <p className="section-subtitle">
            {intl.formatMessage({ id: 'landing.forYou.subtitle' })}
          </p>
          
          <div className="widgets-grid">
            {/* Fan Widget */}
            <div className="widget fan-widget">
              <div className="widget-header">
                <h3>{intl.formatMessage({ id: 'landing.forYou.fan.title' })}</h3>
              </div>
              <div className="widget-content">
                <p>{intl.formatMessage({ id: 'landing.forYou.fan.description' })}</p>
                <ul className="widget-features">
                  {fanFeatures.map((feature) => (
                    <li key={feature}>{stripLeadingEmoji(feature)}</li>
                  ))}
                </ul>
                <div className="widget-stats">
                  <div className="mini-stat">
                    <strong>{intl.formatMessage({ id: 'landing.forYou.fan.stat1Label' })}</strong>
                    <span>{intl.formatMessage({ id: 'landing.forYou.fan.stat1Value' })}</span>
                  </div>
                  <div className="mini-stat">
                    <strong>{intl.formatMessage({ id: 'landing.forYou.fan.stat2Label' })}</strong>
                    <span>{intl.formatMessage({ id: 'landing.forYou.fan.stat2Value' })}</span>
                  </div>
                </div>
              </div>
              <a href={FAN_APP_URL} className="widget-button fan-button" target="_blank" rel="noopener noreferrer">
                <span>{intl.formatMessage({ id: 'landing.forYou.fan.button' })}</span>
                <span className="arrow">→</span>
              </a>
            </div>

            {/* Owner Widget */}
            <div className="widget owner-widget">
              <div className="widget-header">
                <h3>{intl.formatMessage({ id: 'landing.forYou.owner.title' })}</h3>
              </div>
              <div className="widget-content">
                <p>{intl.formatMessage({ id: 'landing.forYou.owner.description' })}</p>
                <ul className="widget-features">
                  {ownerFeatures.map((feature) => (
                    <li key={feature}>{stripLeadingEmoji(feature)}</li>
                  ))}
                </ul>
                <div className="widget-stats">
                  <div className="mini-stat">
                    <strong>{intl.formatMessage({ id: 'landing.forYou.owner.stat1Label' })}</strong>
                    <span>{intl.formatMessage({ id: 'landing.forYou.owner.stat1Value' })}</span>
                  </div>
                  <div className="mini-stat">
                    <strong>{intl.formatMessage({ id: 'landing.forYou.owner.stat2Label' })}</strong>
                    <span>{intl.formatMessage({ id: 'landing.forYou.owner.stat2Value' })}</span>
                  </div>
                </div>
              </div>
              <a href={OWNER_APP_URL} className="widget-button owner-button" target="_blank" rel="noopener noreferrer">
                <span>{intl.formatMessage({ id: 'landing.forYou.owner.button' })}</span>
                <span className="arrow">→</span>
              </a>
            </div>

            {/* Spotter Widget */}
            <div className="widget spotter-widget">
              <div className="widget-header">
                <h3>{intl.formatMessage({ id: 'landing.forYou.spotter.title' })}</h3>
              </div>
              <div className="widget-content">
                <p>{intl.formatMessage({ id: 'landing.forYou.spotter.description' })}</p>
                <ul className="widget-features">
                  {spotterFeatures.map((feature) => (
                    <li key={feature}>{stripLeadingEmoji(feature)}</li>
                  ))}
                </ul>
                <div className="widget-stats">
                  <div className="mini-stat">
                    <strong>{intl.formatMessage({ id: 'landing.forYou.spotter.stat1Label' })}</strong>
                    <span>{intl.formatMessage({ id: 'landing.forYou.spotter.stat1Value' })}</span>
                  </div>
                  <div className="mini-stat">
                    <strong>{intl.formatMessage({ id: 'landing.forYou.spotter.stat2Label' })}</strong>
                    <span>{intl.formatMessage({ id: 'landing.forYou.spotter.stat2Value' })}</span>
                  </div>
                </div>
              </div>
              <a href={SPOTTER_APP_URL} className="widget-button spotter-button" target="_blank" rel="noopener noreferrer">
                <span>{intl.formatMessage({ id: 'landing.forYou.spotter.button' })}</span>
                <span className="arrow">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <h2 className="section-title">{intl.formatMessage({ id: 'landing.howItWorks.title' })}</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>{intl.formatMessage({ id: 'landing.howItWorks.step1.title' })}</h3>
              <p>{intl.formatMessage({ id: 'landing.howItWorks.step1.description' })}</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>{intl.formatMessage({ id: 'landing.howItWorks.step2.title' })}</h3>
              <p>{intl.formatMessage({ id: 'landing.howItWorks.step2.description' })}</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>{intl.formatMessage({ id: 'landing.howItWorks.step3.title' })}</h3>
              <p>{intl.formatMessage({ id: 'landing.howItWorks.step3.description' })}</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Cover */}
      <section className="what-we-cover">
        <div className="container">
          <h2 className="section-title">{intl.formatMessage({ id: 'landing.whatWeCover.title' })}</h2>
          <p className="section-subtitle">
            {intl.formatMessage({ id: 'landing.whatWeCover.subtitle' })}
          </p>
          
          {isMobile ? (
            <div className="coverage-accordion">
              {coverageItems.map((item) => {
                const isOpen = activeCoverageKey === item.key;
                return (
                  <div key={item.key} className={`coverage-accordion-item ${item.cardClass}`}>
                    <button
                      type="button"
                      className="coverage-accordion-header"
                      aria-expanded={isOpen}
                      onClick={() =>
                        setActiveCoverageKey((prev) => (prev === item.key ? null : item.key))
                      }
                    >
                      <div className="coverage-header">
                        <div className="coverage-icon">{item.icon}</div>
                        <h3>{intl.formatMessage({ id: item.titleId })}</h3>
                      </div>
                      <span className="coverage-accordion-toggle">{isOpen ? '–' : '+'}</span>
                    </button>
                    <div className={`coverage-accordion-panel${isOpen ? ' is-open' : ''}`}>
                      <div className="coverage-sports-list">
                        {item.tags.map((tag, index) => {
                          const normalizedTag = stripLeadingEmoji(tag);
                          return (
                            <span key={`${item.key}-${index}`} className="sport-tag" title={normalizedTag}>
                              {normalizedTag}
                            </span>
                          );
                        })}
                      </div>
                      <div className="coverage-moments">
                        <h4>{intl.formatMessage({ id: item.momentsTitleId })}</h4>
                        <ul>
                          {item.moments.map((moment) => (
                            <li key={moment.id}>
                              {intl.formatMessage({ id: moment.id })}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="coverage-grid">
              {coverageItems.map((item) => (
                <div key={item.key} className={`coverage-card ${item.cardClass}`}>
                  <div className="coverage-header">
                    <div className="coverage-icon">{item.icon}</div>
                    <h3>{intl.formatMessage({ id: item.titleId })}</h3>
                  </div>
                  <div className="coverage-sports-list">
                    {item.previewTags.map((tag, index) => (
                      <span
                        key={`${item.key}-${index}`}
                        className={`sport-tag${tag.isMore ? ' sport-tag-more' : ''}${tag.isPlaceholder ? ' sport-tag-placeholder' : ''}`}
                        title={tag.isPlaceholder ? undefined : tag.label}
                      >
                        <span className="sport-tag-label">{tag.label}</span>
                      </span>
                    ))}
                  </div>
                  <div className="coverage-moments">
                    <h4>{intl.formatMessage({ id: item.momentsTitleId })}</h4>
                    <ul>
                      {item.moments.map((moment) => (
                        <li key={moment.id}>
                          {intl.formatMessage({ id: moment.id })}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="coverage-footer">
            <p className="coverage-tagline">
              {intl.formatMessage({ id: 'landing.whatWeCover.footer.text' })}{' '}
              <strong>{intl.formatMessage({ id: 'landing.whatWeCover.footer.boldText' })}</strong>{' '}
              {intl.formatMessage({ id: 'landing.whatWeCover.footer.text2' })}{' '}
              <span className="gradient-text-gold">{intl.formatMessage({ id: 'landing.whatWeCover.footer.gradientText' })}</span>.
            </p>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="container footer-content">
          <span className="footer-brand">HypeDirector</span>
          <a className="footer-email" href="mailto:media@hypedirector.com">
            media@hypedirector.com
          </a>
          <div className="footer-legal">
            <Link className="footer-link" to="/vision">
              Vision
            </Link>
            <Link className="footer-link" to="/privacy">
              Privacy
            </Link>
            <Link className="footer-link" to="/terms">
              Terms
            </Link>
          </div>
          <div className="footer-links">
            <a
              className="footer-link"
              href="https://www.instagram.com/hype.director"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              title="Instagram"
            >
              <svg className="footer-link-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm10 2c1.654 0 3 1.346 3 3v10c0 1.654-1.346 3-3 3H7c-1.654 0-3-1.346-3-3V7c0-1.654 1.346-3 3-3h10zm-5 3a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6zm4.5-.75a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z" />
              </svg>
            </a>
            <a
              className="footer-link"
              href="https://www.linkedin.com/in/media-hypedirector"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              title="LinkedIn"
            >
              <svg className="footer-link-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 8h5v16H0V8zm7.5 0h4.8v2.2h.07c.67-1.27 2.3-2.6 4.73-2.6 5.06 0 6 3.33 6 7.66V24h-5v-7.59c0-1.81-.03-4.15-2.53-4.15-2.53 0-2.92 1.98-2.92 4.02V24h-5V8z" />
              </svg>
            </a>
            <a
              className="footer-link"
              href="https://www.youtube.com/@mediahypedirector"
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube"
              title="YouTube"
            >
              <svg className="footer-link-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M23.5 7.2a3.1 3.1 0 00-2.2-2.2C19.6 4.5 12 4.5 12 4.5s-7.6 0-9.3.5A3.1 3.1 0 00.5 7.2 32.3 32.3 0 000 12a32.3 32.3 0 00.5 4.8 3.1 3.1 0 002.2 2.2c1.7.5 9.3.5 9.3.5s7.6 0 9.3-.5a3.1 3.1 0 002.2-2.2A32.3 32.3 0 0024 12a32.3 32.3 0 00-.5-4.8zM9.6 15.2V8.8L15.8 12l-6.2 3.2z" />
              </svg>
            </a>
          </div>
        </div>
        <AppStoreButtons className="footer-app-store-buttons" />
      </footer>
    </div>
  );
};

